/**
 * SwiperSlider
 * @description Web component used to create sliders, configurable with data-options attribute.
 * @documentation https://swiperjs.com/swiper-api
 * @example
<swiper-slider>
  <div
    class="swiper"
    data-swiper
    data-options='{
      "threshold": 10,
      "loop": false,
      "scrollbar": true,
      "navigation": {
        "nextEl": ".swiper-button-next-{{ section.id }}",
        "prevEl": ".swiper-button-prev-{{ section.id }}"
      },
      "pagination": {
        "el": ".swiper-pagination-{{ section.id }}",
        "type": "bullets"
      },
      "slidesPerView": 1,
      "spaceBetween": 16,
      "breakpoints": {
        "768": {
          "slidesPerView": 2,
          "spaceBetween": 24
        },
        "1024": {
          "slidesPerView": 3,
          "spaceBetween": 32
        }
      }
    }'
  >
    <div class="swiper-wrapper">
      {%- for item in (1..8) -%}
        <div class="swiper-slide" data-swiper-slide-index="{{ forloop.index }}">
          <h2>
            Your slide
          </h2>
        </div>
      {%- endfor -%}
    </div>

    <button
      class="swiper-button-prev swiper-button-prev-{{ section.id }} "
      aria-label="{% render "format-translation", namespace: "accessibility", key: "previous_slide", fallback: "Previous slide" %}"
    >
      {%- render 'global-icon', icon: 'chevron-left', icon_class: 'w-5' -%}
    </button>
    <button
      class="swiper-button-next swiper-button-next-{{ section.id }} "
      aria-label="{% render "format-translation", namespace: "accessibility", key: "next_slide", fallback: "Next slide" %}"
    >
      {%- render 'global-icon', icon: 'chevron-right', icon_class: 'w-5' -%}
    </button>
      
    <div class="swiper-pagination swiper-pagination-{{ section.id }} flex justify-center"></div>
    <div class="swiper-scrollbar"></div>
  </div>
</swiper-slider>
 */

(async () => {
  if (!customElements.get('swiper-slider')) {
    const { default: Swiper } = await import('swiper');
    const { A11y, Navigation, Pagination, Scrollbar, Autoplay } = await import('swiper/modules');

    Swiper.use([A11y, Navigation, Pagination, Scrollbar, Autoplay]);

    class SwiperSlider extends HTMLElement {
      constructor() {
        super();
        this.swiper = this.querySelector('[data-swiper]');

        // Stop when the swiper is not found
        if (!this.swiper) {
          return;
        }

        // Set up ARIA carousel semantics
        this.setupCarouselSemantics();

        // Default to improve user experience
        this.swiperOptions = {
          threshold: 10,
          a11y: {
            enabled: true,
            prevSlideMessage: this.dataset.prevMessage || 'Previous slide',
            nextSlideMessage: this.dataset.nextMessage || 'Next slide',
            firstSlideMessage: this.dataset.firstMessage || 'This is the first slide',
            lastSlideMessage: this.dataset.lastMessage || 'This is the last slide',
            paginationBulletMessage: this.dataset.paginationMessage || 'Go to slide {{index}}',
          },
        };

        // Check if we have extra options on the HTML
        if (this.swiper.dataset.options) {
          const options = JSON.parse(this.swiper.dataset.options);
          if (options) {
            this.swiperOptions = {
              ...this.swiperOptions,
              ...options,
            };
          }
        }

        // Call swiper with selected swiper element and options
        this.swiperInstance = new Swiper(this.swiper, this.swiperOptions);

        // Add slide numbering after initialization
        this.addSlideNumbering();

        // Update slide numbering on slide change
        this.swiperInstance.on('slideChange', () => {
          this.updateSlideNumbering();
        });

        /**
         * Listen to extra events when in Shopify editor
         */
        if (Shopify.designMode) {
          // Update the swiper when the section event is triggerd
          window.addEventListener('shopify:section:load', (event) => {
            this.swiperInstance.update();
            this.addSlideNumbering();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
          });

          // When on block select go to the slide in front-end
          window.addEventListener('shopify:block:select', (event) => {
            this.swiperInstance.update();
            this.addSlideNumbering();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
            this.handleBlockSelect(event);
          });

          // When on block deselect go to the slide in front-end
          window.addEventListener('shopify:block:deselect', (event) => {
            this.swiperInstance.update();
            this.addSlideNumbering();
            if (this.swiperOptions.navigation) {
              this.swiperInstance.navigation.init();
              this.swiperInstance.navigation.update();
            }
            this.handleBlockSelect(event);
          });
        }
      }

      setupCarouselSemantics() {
        // Determine carousel type and label from data attributes or context
        const carouselLabel = this.dataset.carouselLabel || 'Carousel';

        // Add ARIA carousel semantics to the container
        this.setAttribute('role', 'region');
        this.setAttribute('aria-roledescription', 'carousel');
        this.setAttribute('aria-label', carouselLabel);

        // Add group role to the swiper wrapper
        const wrapper = this.swiper.querySelector('.swiper-wrapper');
        if (wrapper) {
          wrapper.setAttribute('role', 'group');
          wrapper.setAttribute('aria-label', 'Slides');
        }
      }

      /**
       * Handles the theme editor block change/edit event
       * @param {Object} event
       */
      handleBlockSelect(event) {
        // Check if the slide index is set
        if (!('swiperSlideIndex' in event.target.dataset)) {
          return;
        }

        // Set the slide index based on loop settings or not
        let swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) - 1;
        if (this.swiperOptions.loop) {
          swipeToSlideIndex = parseInt(event.target.dataset.swiperSlideIndex) + 1;
        }

        // Slide to slide based on the data attribute from the target
        const sliderToUpdate = event.target.closest('[data-swiper]');
        sliderToUpdate.swiper.slideTo(swipeToSlideIndex, 1000);
      }

      addSlideNumbering() {
        if (!this.swiperInstance) return;

        const slides = this.swiper.querySelectorAll('.swiper-slide');
        const totalSlides = slides.length;

        slides.forEach((slide, index) => {
          const slideNumber = index + 1;
          slide.setAttribute('role', 'group');
          slide.setAttribute('aria-roledescription', 'slide');
          slide.setAttribute(
            'aria-label',
            `${slideNumber} ${this.dataset.ofLabel || 'of'} ${totalSlides}`,
          );
        });
      }

      updateSlideNumbering() {
        if (!this.swiperInstance) return;

        const slides = this.swiper.querySelectorAll('.swiper-slide');
        const totalSlides = slides.length;

        slides.forEach((slide, index) => {
          const slideNumber = index + 1;
          slide.setAttribute(
            'aria-label',
            `${slideNumber} ${this.dataset.ofLabel || 'of'} ${totalSlides}`,
          );
        });
      }
    }

    window.SwiperSlider = SwiperSlider;

    customElements.define('swiper-slider', SwiperSlider);
  }
})();
